/**
 * CR550: Region data fetching helpers
 */

import { supabase } from "@/integrations/supabase/client";
import type { Region, MerchantRegionMembership } from "./types";

/** Fetch enabled regions ordered by sort_rank then label */
export async function fetchRegions(): Promise<Region[]> {
  const { data, error } = await supabase
    .from("regions")
    .select("*")
    .eq("enabled", true)
    .order("sort_rank", { ascending: true, nullsFirst: false })
    .order("label", { ascending: true });

  if (error) {
    console.error("[CR550] Failed to fetch regions:", error);
    return [];
  }
  return (data ?? []) as Region[];
}

/**
 * Fetch merchant_keys that belong to a given region.
 * Two-step: get UUIDs from membership view → resolve to merchant_keys.
 */
export async function fetchMerchantKeysForRegion(regionId: string): Promise<Set<string>> {
  // Step 1: Get merchant UUIDs from the membership view
  const { data: membership, error: membershipError } = await supabase
    .from("merchant_region_membership")
    .select("merchant_uuid")
    .eq("region_id", regionId);

  if (membershipError) {
    console.error("[CR550] Failed to fetch merchant region membership:", membershipError);
    return new Set();
  }

  const uuids = [...new Set(
    ((membership ?? []) as MerchantRegionMembership[])
      .map((r) => r.merchant_uuid)
      .filter(Boolean)
  )];

  if (uuids.length === 0) return new Set();

  // Step 2: Resolve UUIDs to merchant_keys
  const { data: merchants, error: merchantsError } = await supabase
    .from("merchants")
    .select("merchant_key")
    .in("id", uuids);

  if (merchantsError) {
    console.error("[CR550] Failed to resolve merchant keys:", merchantsError);
    return new Set();
  }

  return new Set(
    ((merchants ?? []) as { merchant_key: string }[]).map((m) => m.merchant_key)
  );
}
